/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRuleProposalNominatedGeneralised } from 'src/components/TradeRule';
import TradeRuleProposalNominatedGeneralisedPlaceholder from 'src/components/TradeRule/TradeRuleProposalNominatedGeneralised/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getEmptyRetryVariables from './helpers/getEmptyRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

export const tradeRuleProposalNominatedGeneralisedRouteQuery = graphql`
  query TradeRuleProposalNominatedGeneralisedRoute_Query {
    viewer {
      ...TradeRuleProposalNominatedGeneralised_viewer
    }
  }
`;

const tradeRuleProposalNominatedGeneralisedRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    location: PropTypes.shape({
      query: PropTypes.shape({
        counterpartyEmail: PropTypes.string,
        price: PropTypes.string,
        tradeDirection: PropTypes.string,
        tradePointId: PropTypes.string,
      }),
    }),
  }),
});

const queryParamsShape = PropTypes.shape({
  queryCounterpartyEmail: PropTypes.string,
  queryPrice: PropTypes.string,
  queryTradeDirection: PropTypes.string,
  queryProposerTradePointId: PropTypes.string,
});

/**
 * Renders `TradeRuleProposalNominatedGeneralised` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @param {object} props.queryParams - URL query params extracted from `match.location.query`.
 * @returns {React.ReactNode} trade rule proposal nominated generalised route content.
 */
function TradeRuleProposalNominatedGeneralisedPreloadedQueryRenderer({
  routeProps, queryRef, queryParams,
}) {
  const queryData = usePreloadedQuery(tradeRuleProposalNominatedGeneralisedRouteQuery, queryRef);
  return (
    <TradeRuleProposalNominatedGeneralised
      {...routeProps}
      {...queryData}
      {...queryParams}
    />
  );
}

TradeRuleProposalNominatedGeneralisedPreloadedQueryRenderer.propTypes = {
  routeProps: tradeRuleProposalNominatedGeneralisedRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
  queryParams: queryParamsShape.isRequired,
};

/**
 * Hook-safe renderer for the `TradeRuleProposalNominatedGeneralised` route.
 * Extracts the URL query params, keeps the initial found-relay result for
 * first paint, then switches to a `useQueryLoader`-managed query reference
 * when the user retries after an error so Relay can suspend back to the
 * loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade rule proposal nominated generalised route content.
 */
function TradeRuleProposalNominatedGeneralisedRouteRenderer({ routeProps, routeError = null }) {
  const { match } = routeProps || {};
  const { query } = match?.location || {};
  const {
    counterpartyEmail: queryCounterpartyEmail,
    price: queryPrice,
    tradeDirection: queryTradeDirection,
    tradePointId: queryProposerTradePointId,
  } = query || {};
  const queryParams = {
    queryCounterpartyEmail,
    queryPrice,
    queryTradeDirection,
    queryProposerTradePointId,
  };

  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRuleProposalNominatedGeneralisedRouteQuery,
    routeProps,
    {
      logLabel: 'TradeRuleProposalNominatedGeneralisedRoute',
      getRetryVariablesFunc: getEmptyRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      placeholderComponent={TradeRuleProposalNominatedGeneralisedPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRuleProposalNominatedGeneralisedPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
            queryParams={queryParams}
          />
        ) : (
          <TradeRuleProposalNominatedGeneralised
            {...routeProps}
            {...queryParams}
          />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRuleProposalNominatedGeneralisedRouteRenderer.propTypes = {
  routeProps: tradeRuleProposalNominatedGeneralisedRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRuleProposalNominatedGeneralised route state supplied by found-relay.
 * Any network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it before pending props resolve. This route
 * is not entity-keyed, so no `key` is applied.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRuleProposalNominatedGeneralised route content or loading state.
 */
export function tradeRuleProposalNominatedGeneralisedRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRuleProposalNominatedGeneralisedRouteRenderer
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeRuleProposalNominatedGeneralisedRouteRenderer
      routeProps={props}
    />
  );
}

export default TradeRuleProposalNominatedGeneralisedRouteRenderer;
